// Packet definitions + data layer for the document signing workflow.
// A "definition" declares which forms a packet contains and which signature
// slots each requires (by party). createPacket() materializes these into the
// document_packets / packet_forms / signature_slots tables.

import { supabase } from '../supabase';
import { normalizeLang, langFromClient } from './i18n';
import { isDmeOnly, isPrivateTrack } from '../serviceModel';

// party_role groups slots for signing access:
//   external (parent/responsible party, physician) sign via tokenized link
//   internal (rn, agency, administrator, witness) sign in-app
export const PARTY = {
  CLIENT: 'client',          // client / responsible party (external)
  RN: 'rn',                  // registered nurse (internal)
  AGENCY: 'agency',          // agency representative (internal)
  ADMIN: 'administrator',    // administrator/director (internal)
  WITNESS: 'witness',        // AHFC staff witness (internal)
  PHYSICIAN: 'physician',    // physician (external)
  EXTERNAL: 'external',      // anyone else by email — an agency, a staff member, a signer on a client-less request
};

export const PACKET_DEFS = {
  private_intake: {
    label: 'Private Home Care Intake',
    forms: [
      { form_key: 'client_info', form_title: 'Client Information' },
      { form_key: 'form_4', form_title: 'Service Agreement' },
      { form_key: 'forms_5678', form_title: 'Client Acknowledgments' },
      { form_key: 'phi_release', form_title: 'Authorization for Release of Medical Information' },
      { form_key: 'form_2', form_title: 'Intake Assessment' },
      { form_key: 'form_3', form_title: 'Service Plan' },
      { form_key: 'form_9', form_title: 'Medical Fragility / Complexity Screening' },
    ],
    slots: [
      { slot_key: 'form4_client', form_key: 'form_4', party_role: PARTY.CLIENT, signer_label: 'Client / Responsible Party' },
      { slot_key: 'form4_agency', form_key: 'form_4', party_role: PARTY.AGENCY, signer_label: 'Agency Representative' },
      { slot_key: 'ack_client', form_key: 'forms_5678', party_role: PARTY.CLIENT, signer_label: 'Client / Responsible Party' },
      { slot_key: 'form2_rn', form_key: 'form_2', party_role: PARTY.RN, signer_label: 'RN / Qualified Professional' },
      { slot_key: 'form2_client', form_key: 'form_2', party_role: PARTY.CLIENT, signer_label: 'Client / Responsible Party' },
      { slot_key: 'form3_client', form_key: 'form_3', party_role: PARTY.CLIENT, signer_label: 'Client / Responsible Party' },
      { slot_key: 'form3_rn', form_key: 'form_3', party_role: PARTY.RN, signer_label: 'RN / Qualified Professional' },
      { slot_key: 'form9_rn', form_key: 'form_9', party_role: PARTY.RN, signer_label: 'RN / Qualified Professional' },
      { slot_key: 'form9_admin', form_key: 'form_9', party_role: PARTY.ADMIN, signer_label: 'Administrator / Director' },
      { slot_key: 'phi_rep', form_key: 'phi_release', party_role: PARTY.CLIENT, signer_label: 'Patient or Authorized Representative' },
      { slot_key: 'phi_witness', form_key: 'phi_release', party_role: PARTY.WITNESS, signer_label: 'AHFC Staff Witness' },
    ],
  },
  // GAPP Unified Intake Packet — same documents, same page order and the same
  // three signing parties as the SignWell template it replaces:
  //   client  = parent / primary caregiver (external link or in person)
  //   agency  = the AHFC staff member preparing the packet (provider, witness,
  //             nursing-agency representative on every state form)
  //   rn      = the assessing RN (assessment signature page only)
  // State appendices are filled on the original DCH PDFs (gappOverlay.js).
  gapp_intake: {
    label: 'GAPP Unified Intake Packet',
    forms: [
      { form_key: 'phi_release', form_title: 'Authorization for Release of Medical Information' },
      { form_key: 'nacp_signature', form_title: 'Assessment, Care Plan & Caregiver Training — Signature Page' },
      { form_key: 'appendix_n', form_title: 'Appendix N — Admission/Continued Stay Checklist' },
      { form_key: 'appendix_i', form_title: 'Appendix I — Letter of Understanding' },
      { form_key: 'appendix_g', form_title: 'Appendix G — Freedom of Choice' },
      { form_key: 'appendix_h', form_title: 'Appendix H — Medical Necessity Statement' },
      { form_key: 'appendix_u', form_title: 'Appendix U — Caregiver Attestation' },
      { form_key: 'appendix_s', form_title: 'Appendix S — Shared Hours Request' },
      { form_key: 'appendix_v', form_title: 'Appendix V — Seizure Observation Log' },
    ],
    slots: [
      { slot_key: 'phi_rep', form_key: 'phi_release', party_role: PARTY.CLIENT, signer_label: 'Patient or Authorized Representative' },
      { slot_key: 'phi_witness', form_key: 'phi_release', party_role: PARTY.AGENCY, signer_label: 'AHFC Staff Witness' },
      { slot_key: 'nacp_parent', form_key: 'nacp_signature', party_role: PARTY.CLIENT, signer_label: 'Parent / Guardian' },
      { slot_key: 'nacp_rn', form_key: 'nacp_signature', party_role: PARTY.RN, signer_label: 'Registered Nurse' },
      { slot_key: 'i_caregiver', form_key: 'appendix_i', party_role: PARTY.CLIENT, signer_label: "Member's Caregiver / Primary Caregiver" },
      { slot_key: 'i_agency', form_key: 'appendix_i', party_role: PARTY.AGENCY, signer_label: 'Nursing Agency Representative' },
      { slot_key: 'g_provider', form_key: 'appendix_g', party_role: PARTY.AGENCY, signer_label: 'GAPP Provider (Verification)' },
      { slot_key: 'g_member', form_key: 'appendix_g', party_role: PARTY.CLIENT, signer_label: 'Member (signed by caregiver — minor)' },
      { slot_key: 'g_caregiver', form_key: 'appendix_g', party_role: PARTY.CLIENT, signer_label: 'Authorized Representative' },
      { slot_key: 'g_witness', form_key: 'appendix_g', party_role: PARTY.AGENCY, signer_label: 'Witness' },
      { slot_key: 'h_caregiver', form_key: 'appendix_h', party_role: PARTY.CLIENT, signer_label: 'Primary Caregiver' },
      { slot_key: 'h_agency', form_key: 'appendix_h', party_role: PARTY.AGENCY, signer_label: 'Nursing Agency' },
      { slot_key: 'u_caregiver', form_key: 'appendix_u', party_role: PARTY.CLIENT, signer_label: 'Caregiver' },
      { slot_key: 'u_agency', form_key: 'appendix_u', party_role: PARTY.AGENCY, signer_label: 'GAPP Agency Personnel' },
      { slot_key: 's_caregiver', form_key: 'appendix_s', party_role: PARTY.CLIENT, signer_label: 'Caregiver' },
    ],
  },
  gapp_transfer: {
    label: 'GAPP Transfer',
    forms: [
      { form_key: 'appendix_r', form_title: 'Appendix R — GAPP Transfer Request' },
      { form_key: 'phi_release', form_title: 'Authorization for Release of Medical Information' },
    ],
    slots: [
      { slot_key: 'r_caregiver', form_key: 'appendix_r', party_role: PARTY.CLIENT, signer_label: 'Caregiver' },
      { slot_key: 'phi_rep', form_key: 'phi_release', party_role: PARTY.CLIENT, signer_label: 'Patient or Authorized Representative' },
      { slot_key: 'phi_witness', form_key: 'phi_release', party_role: PARTY.AGENCY, signer_label: 'AHFC Staff Witness' },
    ],
  },
};

// ── Documents & request kinds ────────────────────────────────────────────────
// Every signable GAPP document, with the signatures it carries, derived from
// the two packet definitions above so there is one place the slots are declared.
export const GAPP_DOCS = (() => {
  const docs = {};
  ['gapp_intake', 'gapp_transfer'].forEach((type) => {
    PACKET_DEFS[type].forms.forEach((f) => {
      docs[f.form_key] = docs[f.form_key] || { form_key: f.form_key, form_title: f.form_title, slots: [] };
      PACKET_DEFS[type].slots.filter((sl) => sl.form_key === f.form_key).forEach((sl) => {
        if (!docs[f.form_key].slots.find((x) => x.slot_key === sl.slot_key)) docs[f.form_key].slots.push(sl);
      });
    });
  });
  return docs;
})();

// Short names for filing: "G005 – 2026-09 – Appendix H – signed.pdf".
export const DOC_SHORT_NAME = {
  phi_release: 'PHI Release', nacp_signature: 'Assessment sign-off',
  appendix_n: 'Appendix N', appendix_i: 'Appendix I', appendix_g: 'Appendix G', appendix_h: 'Appendix H',
  appendix_u: 'Appendix U', appendix_s: 'Appendix S', appendix_v: 'Appendix V', appendix_r: 'Appendix R',
  client_info: 'Client Information', form_4: 'Service Agreement', forms_5678: 'Acknowledgments',
  form_2: 'Intake Assessment', form_3: 'Service Plan', form_9: 'MF-MC Screening',
};

// What a request starts with. Intake and renewal are the same forms; renewal
// also carries the continued-stay notes DCH wants, attached but not signed.
// Transfer is Appendix R plus our own two documents. LON and Other start
// empty: the sender uploads the file and places the fields.
export const REQUEST_KINDS = {
  intake:   { label: 'Family forms — initial',   packet_type: 'gapp_intake',   cycle_kind: 'initial',
              forms: PACKET_DEFS.gapp_intake.forms.map((f) => f.form_key),
              blurb: 'First GAPP submission. The PHI release, the assessment sign-off, and Appendices N, I, G, H, U, S, V.' },
  renewal:  { label: 'Family forms — renewal',  packet_type: 'gapp_intake',   cycle_kind: 'renewal',
              forms: PACKET_DEFS.gapp_intake.forms.map((f) => f.form_key),
              attachments: [
                { key: 'sixty_day', label: '60-day RN clinical summary', match: /60.?day|clinical summary/i },
                { key: 'supervisory', label: 'Supervisory visit notes', match: /supervis/i },
                { key: 'caregiver_notes', label: 'Caregiver (shift) notes — last 3 months', match: /shift|caregiver note|care note/i },
              ],
              blurb: 'Same forms as intake, plus the caregiver and supervisory notes DCH wants for continued stay.' },
  transfer: { label: 'Family forms — transfer', packet_type: 'gapp_transfer', cycle_kind: 'transfer',
              forms: ['appendix_r', 'phi_release', 'nacp_signature'],
              blurb: 'Appendix R, with our own PHI release and the assessment sign-off.' },
  lon:      { label: 'Letter of Notification', packet_type: 'gapp_intake',   cycle_kind: null, forms: [], upload: true,
              blurb: 'The Letter of Notification from DCH, for the parent to acknowledge. Upload the letter and place the signature.' },
  other:    { label: 'Any document',  packet_type: 'gapp_intake',   cycle_kind: null, forms: [], upload: true,
              blurb: "Any file from the client's folder or your computer. Place signature, date and name fields where they go." },
};

// Which party signs an uploaded document's placed field. Slot per (document, party).
export const UPLOAD_PARTIES = [
  { party_role: PARTY.CLIENT, label: 'Parent / caregiver' },
  { party_role: PARTY.AGENCY, label: 'Agency (AHFC staff)' },
  { party_role: PARTY.RN, label: 'Registered Nurse' },
];
// A request with no client: the recipient signs by email, AHFC signs in CAERA.
export const GENERAL_PARTIES = [
  { party_role: PARTY.EXTERNAL, label: 'Recipient' },
  { party_role: PARTY.AGENCY, label: 'AHFC' },
];
// Where a client-less request files: 6 – Client Folder / _General / e-Signatures.
export const GENERAL_CLIENT_CODE = '_General';
export const generalClientStub = (packet) => ({ id: null, client_id: GENERAL_CLIENT_CODE, first_name: packet?.subject || packet?.recipient_name || 'General', last_name: '', language: 'English' });

// Editable fields per form for the in-app builder. type: text|textarea|date|checks.
// Keep in sync with the PDF template field keys. Tables (ADLs/risks/criteria) are
// edited as textarea JSON for now — refine to row editors later.
export const FORM_FIELDS = {
  // Client Information — editable in the builder; keys ARE real clients columns,
  // so saving writes straight back to the client record (a second way to update
  // the DB). HFRD .09(4)(a)(1) record element.
  client_info: [
    { key: 'first_name', label: 'First Name', type: 'text' },
    { key: 'last_name', label: 'Last Name', type: 'text' },
    { key: 'date_of_birth', label: 'Date of Birth (YYYY-MM-DD)', type: 'text' },
    { key: 'gender', label: 'Gender', type: 'text' },
    { key: 'address', label: 'Street Address', type: 'text' },
    { key: 'city', label: 'City', type: 'text' },
    { key: 'state', label: 'State', type: 'text' },
    { key: 'zip', label: 'ZIP', type: 'text' },
    { key: 'language', label: 'Primary Language', type: 'text' },
    { key: 'insurance_name', label: 'Funding / Insurance', type: 'text' },
    { key: 'guardian_first_name', label: 'Responsible Party — First Name', type: 'text' },
    { key: 'guardian_last_name', label: 'Responsible Party — Last Name', type: 'text' },
    { key: 'guardian_relationship', label: 'Responsible Party — Relationship', type: 'text' },
    { key: 'guardian_phone', label: 'Responsible Party — Phone', type: 'text' },
    { key: 'guardian_email', label: 'Responsible Party — Email', type: 'text' },
    { key: 'guardian_address', label: 'Responsible Party — Address', type: 'text' },
    { key: 'emergency_first_name', label: 'Emergency Contact — First Name', type: 'text' },
    { key: 'emergency_last_name', label: 'Emergency Contact — Last Name', type: 'text' },
    { key: 'emergency_relationship', label: 'Emergency Contact — Relationship', type: 'text' },
    { key: 'emergency_phone', label: 'Emergency Contact — Phone', type: 'text' },
    { key: 'emergency_email', label: 'Emergency Contact — Email', type: 'text' },
    { key: 'emergency_address', label: 'Emergency Contact — Address', type: 'text' },
    { key: 'doctor_name', label: 'Primary Physician', type: 'text' },
    { key: 'doctor_phone', label: 'Physician Phone', type: 'text' },
    { key: 'doctor_address', label: 'Physician Address', type: 'text' },
    { key: 'diagnosis_summary', label: 'Primary Diagnosis', type: 'textarea' },
    { key: 'allergies', label: 'Allergies / Precautions', type: 'text' },
    { key: 'current_medications', label: 'Current Medications', type: 'textarea' },
    { key: 'referral_date', label: 'Referral Date', type: 'text' },
    { key: 'referral_source', label: 'Referral Source', type: 'text' },
  ],
  form_4: [
    { key: 'serviceStart', label: 'Service Start Date', type: 'text' },
    { key: 'initialContact', label: 'Initial Contact / Referral Date', type: 'text' },
    { key: 'serviceRequest', label: "Client's Request for Service", type: 'textarea' },
    { key: 'services', label: 'Services Provided', type: 'checks', options: [
      'Nursing Services', 'Personal Care Services', 'Companion / Sitter Services',
      'Homemaker Services', 'Respite Services', 'Aide Services',
      'Personal Support Services', 'Personal Care Aide Services'] },
    { key: 'frequency', label: 'Frequency & Duration', type: 'text' },
    { key: 'rate', label: 'Rate of Service', type: 'text' },
  ],
  forms_5678: [],  // acknowledgment text is fixed; only signature required
  form_2: [
    { key: 'referralDate', label: 'Referral Date', type: 'text' },
    { key: 'referralSource', label: 'Referral Source', type: 'text' },
    { key: 'adls', label: 'Activities of Daily Living (ADLs)', type: 'table',
      cols: ['activity', 'status', 'comments'], colLabels: ['Activity', 'Status', 'Comments'],
      colTypes: ['text', 'select', 'text'],
      colOptions: { status: ['Independent', 'Needs Assistance', 'Dependent'] } },
    { key: 'iadls', label: 'Instrumental ADLs (IADLs)', type: 'table',
      cols: ['activity', 'status', 'comments'], colLabels: ['Activity', 'Status', 'Comments'],
      colTypes: ['text', 'select', 'text'],
      colOptions: { status: ['Independent', 'Needs Assistance', 'Dependent'] } },
    { key: 'homeAccessibility', label: 'Environment — Home Accessibility', type: 'text' },
    { key: 'fallRisks', label: 'Environment — Fall Risks', type: 'text' },
    { key: 'specialEquipment', label: 'Environment — Special Equipment', type: 'text' },
    { key: 'otherSafety', label: 'Environment — Other Safety Concerns', type: 'text' },
    { key: 'nursingNeeds', label: 'Medical / Nursing Needs', type: 'table',
      cols: ['service', 'required', 'notes'], colLabels: ['Service', 'Required?', 'Notes'],
      colTypes: ['text', 'select', 'text'],
      colOptions: { required: ['Yes', 'No'] } },
    { key: 'risks', label: 'Risk Assessment', type: 'table',
      cols: ['type', 'atRisk', 'notes'], colLabels: ['Risk Type', 'At Risk?', 'Notes / Mitigation'],
      colTypes: ['text', 'select', 'text'],
      colOptions: { atRisk: ['Yes', 'No', 'Low'] } },
    { key: 'assessmentSummary', label: 'Assessment Summary & Plan', type: 'textarea' },
  ],
  form_3: [
    { key: 'preparedBy', label: 'Prepared By (RN)', type: 'text' },
    { key: 'primaryDx', label: 'Primary Diagnosis', type: 'text' },
    { key: 'allergies', label: 'Allergies / Precautions', type: 'text' },
    { key: 'functionalLimits', label: 'Functional Limitations', type: 'text' },
    { key: 'assistiveDevices', label: 'Assistive Devices', type: 'text' },
    { key: 'goals', label: 'Goals & Outcomes', type: 'table',
      cols: ['goal', 'outcome', 'timeline'], colLabels: ['Goal', 'Desired Outcome', 'Timeline'] },
    { key: 'planServices', label: 'Services to Be Provided', type: 'table',
      cols: ['type', 'frequency', 'duration', 'role'],
      colLabels: ['Service Type', 'Frequency', 'Duration', 'Provider Role'] },
  ],
  form_9: [
    { key: 'mfmcDate', label: 'Assessment Date', type: 'text' },
    { key: 'mfmcAssessor', label: 'Assessed By (RN)', type: 'text' },
    { key: 'mfmcCriteria', label: 'MF/MC Criteria', type: 'table',
      cols: ['indicator', 'present', 'notes'], colLabels: ['Indicator', 'Present?', 'Notes'],
      colTypes: ['text', 'select', 'text'], colOptions: { present: ['Yes', 'No'] } },
    { key: 'mfmcNarrative', label: 'Clinical Narrative', type: 'textarea' },
    { key: 'mfmcDetermination', label: 'Determination', type: 'select', options: ['yes', 'no'] },
  ],
  phi_release: [
    { key: 'phiReleased', label: 'Information to be Released', type: 'checks', options: [
      'Medical history/diagnoses', 'Physician orders/POC', 'Nursing notes', 'Medications',
      'Discharge summaries', 'Therapy records', 'Lab/imaging results', 'GAPP documentation'] },
    { key: 'phiValid', label: 'Authorization valid', type: 'select', options: ['Until end of home care services', 'Other'] },
    { key: 'phiValidOther', label: 'Other expiration (if "Other")', type: 'text' },
    { key: 'phiSignerType', label: 'Signer is the', type: 'select', options: ['Parent / Legal Guardian', 'Patient (18+)', 'Legal Representative / POA'] },
    { key: 'phiPrintedName', label: 'Printed Name of Signer', type: 'text' },
    { key: 'phiRelationship', label: 'Relationship to Patient', type: 'text' },
  ],

  // ── GAPP forms ──────────────────────────────────────────────────────────────
  // Field keys below drive the overlay onto the original DCH PDFs (gappFieldMaps.js).
  nacp_signature: [
    { key: 'nacpVisitDate', label: 'Date of Nursing Assessment (in-home visit)', type: 'text' },
    { key: 'nacpRnLicense', label: 'RN License #', type: 'text' },
  ],
  appendix_g: [],  // fixed informed-consent text; signatures only
  appendix_h: [
    { key: 'hWeight', label: "Member's Weight", type: 'text' },
    { key: 'hDfcsCustody', label: 'Child in DFCS Custody', type: 'select', options: ['No', 'Yes'] },
    { key: 'hDfcsWorker', label: 'Name / Address of DFCS Worker', type: 'text' },
    { key: 'hSkilledHrs', label: 'Skilled Nursing Hours Currently Receiving', type: 'text' },
    { key: 'hPssHrs', label: 'PSS Hours Currently Receiving', type: 'text' },
    { key: 'hOtherMedicaid', label: 'Services from any Medicaid or other service program', type: 'select', options: ['No', 'Yes'] },
    { key: 'hServicesProvided', label: 'Services currently being provided', type: 'text' },
    { key: 'hFamilyPaid', label: 'Services provided by a family paid caregiver', type: 'select', options: ['No', 'Yes'] },
    { key: 'hInSchool', label: 'Member in School', type: 'select', options: ['No', 'Yes'] },
    { key: 'hSchoolHrs', label: 'Hrs/Day in School', type: 'text' },
    { key: 'hIep', label: 'IEP', type: 'text' },
    { key: 'hNurseSchool', label: 'Personal nurse in attendance during school day', type: 'select', options: ['No', 'Yes'] },
    { key: 'hRationale', label: 'If school age and not in school — rationale', type: 'text' },
    { key: 'hPrivateIns', label: 'Private Insurance', type: 'select', options: ['No', 'Yes'] },
    { key: 'hOtherGapp', label: 'Other GAPP Members in Home', type: 'select', options: ['No', 'Yes'] },
    { key: 'hOtherGappNames', label: 'Names of other GAPP members (if yes)', type: 'text' },
    { key: 'hCompetent', label: "Caregiver has documented competence in member's care", type: 'select', options: ['Yes', 'No'] },
    { key: 'hPa', label: 'PA #', type: 'text' },
  ],
  appendix_i: [],  // fixed letter-of-understanding text; signatures only
  appendix_n: [
    { key: 'nAddress', label: "Member's Address", type: 'text' },
    { key: 'nDfcsCounty', label: 'DFCS County', type: 'text' },
    { key: 'nDfcsAddress', label: 'DFCS Address', type: 'text' },
    { key: 'nDfcsSupervisor', label: 'DFCS Supervisor required to sign all paperwork', type: 'text' },
    { key: 'nPhysician', label: "Physician's Name and Address", type: 'text' },
    { key: 'nChecklist', label: 'Checklist — items included/attached', type: 'checks', options: [
      'Physician Plan of Treatment - Appendix T', 'Letter of Understanding (yearly)',
      'Freedom of Choice Form (yearly)', 'Current IEP (if there is one)',
      'Attestation for Transfers and Shared Hours - Appendix U',
      'Caregiver Skills Checklist Teaching Plan for Skilled Nursing',
      'Most recent 3 months of Narrative Nursing notes',
      'Medical Necessity Statement for Admission or Continued Stay',
      'Seizure Logs (if requesting hours for seizure activity)',
      'Physician Office Visit Notes', '60 Day Summary', 'Nursing Care Plan', 'Supervisory Visit Notes'] },
    { key: 'nAgencyRep', label: 'Nursing Agency Representative (printed)', type: 'text' },
    { key: 'nPacketDate', label: 'Date of Complete Packet', type: 'text' },
    { key: 'nAdmission', label: 'Admission', type: 'select', options: ['New Admission', 'Continued Stay'] },
  ],
  appendix_s: [
    { key: 'sCurrentHrs', label: 'Current GAPP Hrs Approved', type: 'text' },
    { key: 'sAgency1Name', label: 'Agency One — Name', type: 'text' },
    { key: 'sAgency1Contact', label: 'Agency One — Contact / Phone', type: 'text' },
    { key: 'sAgency1Hrs', label: 'Agency One — Hours Requested', type: 'text' },
    { key: 'sAgency2Name', label: 'Agency Two — Name', type: 'text' },
    { key: 'sAgency2Contact', label: 'Agency Two — Contact / Phone', type: 'text' },
    { key: 'sAgency2Hrs', label: 'Agency Two — Hours Requested', type: 'text' },
    { key: 'sPaExp', label: 'PA Expiration Date', type: 'text' },
    { key: 'sSharedStart', label: 'Shared Hours Start Date', type: 'text' },
    { key: 'sCaregiverPhone', label: "Caregiver's Phone Number", type: 'text' },
  ],
  appendix_u: [],  // fixed attestation text; signatures only
  appendix_v: [
    { key: 'vSeizures', label: 'Seizure Log (one row per seizure)', type: 'table',
      cols: ['date', 'time', 'duration', 'type', 'intervention', 'response', 'reporter'],
      colLabels: ['Date', 'Time', 'Duration', 'Seizure Type', 'Intervention', 'Response', 'Reported By'] },
  ],
  appendix_r: [
    { key: 'rTransferDate', label: 'Date of Transfer Request', type: 'text' },
    { key: 'rCurrentAgency', label: 'Current GAPP Agency', type: 'text' },
    { key: 'rCurrentContact', label: 'Current Agency Contact / Phone', type: 'text' },
    { key: 'rHrsApproved', label: 'GAPP Hrs Approved', type: 'text' },
    { key: 'rPaExp', label: 'PA Expiration Date', type: 'text' },
    { key: 'rSubmissionDate', label: 'Date of Appendix Submission', type: 'text' },
    { key: 'rReason', label: 'Reason for Transfer', type: 'textarea' },
    { key: 'rReceivingAgency', label: 'Receiving GAPP Agency', type: 'text' },
    { key: 'rDma80Date', label: 'DMA 80 Submission Date', type: 'text' },
    { key: 'rCaregiverPhone', label: "Caregiver's Phone Number", type: 'text' },
  ],
};

// Snapshot client fields for prefill. Maps the real clients schema.
function snapshotClient(client) {
  return {
    coordinator: '',  // set by staff in the builder
    captured_at: new Date().toISOString(),
  };
}

// Per-form field_data seeded from client columns so the builder starts populated.
// Only clearly-mappable fields are seeded; staff complete the rest.
export function prefillForms(client) {
  if (!client) return {};
  return {
    form_2: {
      referralDate: client.referral_date || '',
      referralSource: client.referral_source || '',
    },
    form_3: {
      primaryDx: client.diagnosis_summary || '',
      allergies: client.allergies || (client.has_allergies ? '' : 'NKDA'),
    },
    phi_release: {
      phiReleased: ['Medical history/diagnoses', 'Physician orders/POC', 'Nursing notes', 'Medications',
        'Discharge summaries', 'Therapy records', 'Lab/imaging results', 'GAPP documentation'],
      phiValid: 'Until end of home care services',
      phiSignerType: 'Parent / Legal Guardian',
      phiPrintedName: [client.guardian_first_name, client.guardian_last_name].filter(Boolean).join(' ') || client.responsible_person_name || '',
      phiRelationship: client.guardian_relationship || '',
    },
    nacp_signature: {
      nacpVisitDate: client.nurse_assessment_date || '',
    },
    // GAPP — Appendix H prefilled from the client record
    appendix_h: {
      hWeight: client.weight_lbs ? `${client.weight_lbs} lbs` : (client.weight || ''),
      hInSchool: client.in_school ? 'Yes' : 'No',
      hSchoolHrs: client.school_hours_per_day ? `${client.school_hours_per_day}` : '',
      hNurseSchool: client.personal_nurse_in_school ? 'Yes' : 'No',
      hIep: (client.has_iep || client.iep_provided) ? 'Yes' : 'No',
      hPrivateIns: client.private_insurance ? 'Yes' : 'No',
      hOtherGapp: client.gapp_household_member ? 'Yes' : 'No',
      hDfcsCustody: client.in_dfcs_custody ? 'Yes' : 'No',
      hDfcsWorker: client.dfcs_case_manager || '',
      hPa: client.dma_80_pa_number || '',
    },
    appendix_n: {
      nAddress: [client.address, client.city, client.state, client.zip].filter(Boolean).join(', '),
      nDfcsCounty: client.in_dfcs_custody ? (client.county || '') : 'N/A',
      nPhysician: [client.doctor_name, client.doctor_address].filter(Boolean).join(' — '),
      nAdmission: 'New Admission',
    },
    appendix_s: { sPaExp: client.pa_expiry_date || '' },
    appendix_r: {
      rCurrentAgency: 'A Heart For Care', rHrsApproved: '', rPaExp: client.pa_expiry_date || '',
    },
  };
}

// ── send guard ───────────────────────────────────────────────────────────────
// What must be filled before a request goes to the parent. The parent owns
// only their signatures and the PHI choices; every other blank is the
// coordinator's, and a form must never go out with one of these empty.
export const REQUIRED_FIELDS = {
  phi_release:    ['phiPrintedName', 'phiRelationship'],
  nacp_signature: ['nacpVisitDate'],
  appendix_n:     ['nPhysician', 'nAdmission'],
  appendix_h:     ['hWeight', 'hDfcsCustody', 'hInSchool', 'hPrivateIns', 'hOtherGapp'],
  appendix_r:     ['rHrsApproved', 'rPaExp', 'rReason', 'rReceivingAgency'],
};
const CLIENT_REQUIRED = [
  ['first_name', 'Client first name'], ['last_name', 'Client last name'], ['date_of_birth', 'Date of birth'],
  ['medicaid_id', 'Medicaid ID'], ['address', 'Address'],
];
const empty = (v) => v == null || (Array.isArray(v) ? v.length === 0 : String(v).trim() === '');

// forms: [{ form_key, field_data }] (a wizard passes the prefill; the request
// page passes its live field state). Returns [{ form_key, key, label }].
export function missingRequired({ client, forms, gapp = true }) {
  const out = [];
  CLIENT_REQUIRED.forEach(([k, label]) => {
    if (k === 'medicaid_id' && !gapp) return;
    if (empty(client?.[k])) out.push({ form_key: 'client', key: k, label });
  });
  const guardian = [client?.guardian_first_name, client?.guardian_last_name].filter(Boolean).join(' ') || client?.responsible_person_name;
  if (empty(guardian)) out.push({ form_key: 'client', key: 'guardian', label: 'Parent / guardian name' });
  (forms || []).forEach((f) => {
    const req = REQUIRED_FIELDS[f.form_key] || [];
    const fields = FORM_FIELDS[f.form_key] || [];
    req.forEach((key) => {
      if (empty((f.field_data || {})[key])) out.push({ form_key: f.form_key, key, label: (fields.find((x) => x.key === key) || {}).label || key });
    });
  });
  return out;
}

// Create a new packet with its forms and signature slots.
// formKeys (optional) picks a subset / ordering of the packet type's documents;
// kind / cycleId / title describe the request it belongs to.
export async function createPacket({ clientId, packetType, client, language, formKeys, kind, cycleId, title, recipient, subject }) {
  const def = PACKET_DEFS[packetType];
  if (!def) throw new Error(`Unknown packet type: ${packetType}`);

  const { data: { user } } = await supabase.auth.getUser();

  const { data: packet, error: pErr } = await supabase
    .from('document_packets')
    .insert({
      client_id: clientId || null,
      packet_type: packetType,
      status: 'draft',
      // Packet language follows the client's Language unless the builder overrides it.
      language: normalizeLang(language || (client ? langFromClient(client) : 'en')),
      created_by: user?.id || null,
      prefilled_data: client ? snapshotClient(client) : null,
      request_kind: kind || null,
      cycle_id: cycleId || null,
      title: title || null,
      recipient_name: recipient?.name || null,
      recipient_email: recipient?.email || null,
      subject: subject || null,
    })
    .select()
    .single();
  if (pErr) throw pErr;
  if (!packet) throw new Error('Packet insert returned no row (check sign-in / permissions).');

  // An explicit list is authoritative — an empty one is a request that starts
  // with nothing but what gets uploaded (LON, Other).
  const keys = Array.isArray(formKeys) ? formKeys : def.forms.map((f) => f.form_key);
  const seed = client ? prefillForms(client) : {};
  const formRows = keys.map((k, i) => {
    const doc = GAPP_DOCS[k] || def.forms.find((f) => f.form_key === k) || { form_key: k, form_title: k };
    return { packet_id: packet.id, form_key: k, form_title: doc.form_title, sort_order: i, field_data: seed[k] || {} };
  });
  if (formRows.length) {
    const { error: fErr } = await supabase.from('packet_forms').insert(formRows);
    if (fErr) throw fErr;
  }

  const slotRows = [];
  keys.forEach((k) => {
    const slots = (GAPP_DOCS[k] || {}).slots || def.slots.filter((sl) => sl.form_key === k);
    slots.forEach((sl) => slotRows.push({
      packet_id: packet.id, form_key: sl.form_key, slot_key: sl.slot_key,
      party_role: sl.party_role, signer_label: sl.signer_label, sort_order: slotRows.length, required: true,
    }));
  });
  if (slotRows.length) {
    const { error: sErr } = await supabase.from('signature_slots').insert(slotRows);
    if (sErr) throw sErr;
  }

  return packet;
}

// ── uploaded documents (LON, Other) ──────────────────────────────────────────
// Adds an uploaded file as a document of the request. fields = placed fields
// [{ kind, party, page, x, y, w, h }]; a signature slot is created per party
// that has at least one field, keyed "<form_key>:<party>".
// signerNames: who was named for a staff line when the request was made (the
// LON's agency line) — prefilled on the request page, still editable there.
// title: what the document is called to the signer and in Drive, when the file
// name is not it (an LON filed as G058_LON_<PA>_<date>.pdf is "Letter of
// Notification" to the parent).
export async function addUploadForm(packetId, { sourceFileId, name, fields = [], signerNames = null, title: shownAs = null }) {
  const { data: existing } = await supabase.from('packet_forms').select('sort_order').eq('packet_id', packetId).order('sort_order', { ascending: false }).limit(1);
  const sort = ((existing && existing[0]?.sort_order) ?? -1) + 1;
  const formKey = `upload:${sourceFileId}`;
  const title = shownAs || String(name || 'Document').replace(/\.pdf$/i, '');
  const { data: form, error } = await supabase.from('packet_forms').insert({
    packet_id: packetId, form_key: formKey, form_title: title, sort_order: sort,
    field_data: { sourceFileId, name, fields, ...(signerNames ? { signerNames } : {}) }, source_file_id: sourceFileId, source_name: name,
  }).select().single();
  if (error) throw error;
  await syncUploadSlots(packetId, formKey, fields);
  return form;
}

export async function setUploadFields(packetId, form, fields) {
  const { error } = await supabase.from('packet_forms')
    .update({ field_data: { ...(form.field_data || {}), fields } }).eq('id', form.id);
  if (error) throw error;
  await syncUploadSlots(packetId, form.form_key, fields);
}

async function syncUploadSlots(packetId, formKey, fields) {
  const parties = [...new Set((fields || []).map((f) => f.party).filter(Boolean))];
  const { data: have } = await supabase.from('signature_slots').select('id, party_role, signed_at').eq('packet_id', packetId).eq('form_key', formKey);
  const keep = new Set();
  for (const p of parties) {
    const row = (have || []).find((h) => h.party_role === p);
    if (row) { keep.add(row.id); continue; }
    const label = ([...UPLOAD_PARTIES, ...GENERAL_PARTIES].find((u) => u.party_role === p) || {}).label || p;
    const { data: ins, error } = await supabase.from('signature_slots').insert({
      packet_id: packetId, form_key: formKey, slot_key: `${formKey}:${p}`, party_role: p, signer_label: label, sort_order: 999, required: true,
    }).select('id').single();
    if (error) throw error;
    keep.add(ins.id);
  }
  // a party whose fields were all removed loses its slot — unless it already signed
  const stale = (have || []).filter((h) => !keep.has(h.id) && !h.signed_at).map((h) => h.id);
  if (stale.length) await supabase.from('signature_slots').delete().in('id', stale);
}

export async function removeForm(packetId, form) {
  const { data: signed } = await supabase.from('signature_slots').select('id').eq('packet_id', packetId).eq('form_key', form.form_key).not('signed_at', 'is', null).limit(1);
  if (signed && signed.length) throw new Error('This document already carries a signature; void the request instead.');
  await supabase.from('signature_slots').delete().eq('packet_id', packetId).eq('form_key', form.form_key);
  const { error } = await supabase.from('packet_forms').delete().eq('id', form.id);
  if (error) throw error;
}

// ── attachments (not signed) ─────────────────────────────────────────────────
export async function addAttachments(packetId, items) {
  if (!items?.length) return [];
  const rows = items.map((a) => ({ packet_id: packetId, drive_file_id: a.drive_file_id || a.id, name: a.name, source_path: a.source_path || a.path || null }));
  const { data, error } = await supabase.from('packet_attachments').insert(rows).select();
  if (error) throw error;
  return data || [];
}
export const listAttachments = async (packetId) => {
  const { data } = await supabase.from('packet_attachments').select('*').eq('packet_id', packetId).order('created_at');
  return data || [];
};
export const removeAttachment = (id) => supabase.from('packet_attachments').delete().eq('id', id);

// ── delete / void ────────────────────────────────────────────────────────────
// A draft nobody has signed is deleted outright (forms, slots, links and
// attachments cascade). Anything with a signature on it is voided instead, so
// the record of who signed what is never erased.
export async function deleteOrVoidPacket(packetId) {
  const { data: signed } = await supabase.from('signature_slots').select('id').eq('packet_id', packetId).not('signed_at', 'is', null).limit(1);
  if (signed && signed.length) {
    const { error } = await supabase.from('document_packets').update({ status: 'voided' }).eq('id', packetId);
    if (error) throw error;
    return 'voided';
  }
  const { error } = await supabase.from('document_packets').delete().eq('id', packetId);
  if (error) throw error;
  return 'deleted';
}

export async function voidPacket(packetId) {
  const { error } = await supabase.from('document_packets').update({ status: 'voided' }).eq('id', packetId);
  if (error) throw error;
}

// Record where one document of a request was filed.
export async function recordFormFile(formId, { fileId, webViewLink }) {
  const { error } = await supabase.from('packet_forms').update({ final_pdf_id: fileId, final_pdf_url: webViewLink }).eq('id', formId);
  if (error) throw error;
}

// Where "Complete Assessment" should go for a client. GAPP uses the dedicated
// nurse-assessment tool; Private uses the Intake Assessment inside their Private
// Home Care Intake packet (create one if none exists yet).
export async function resolveAssessmentPath(client) {
  // Supplies only — no in-home care to assess. Callers gate the button on
  // isDmeOnly(); returning null keeps a stray call from navigating anywhere.
  if (isDmeOnly(client)) return null;
  const isPrivate = isPrivateTrack(client);
  if (!isPrivate) return `/clients/${client.id}/assessment`;
  const { data } = await supabase.from('document_packets')
    .select('id').eq('client_id', client.id).eq('packet_type', 'private_intake')
    .neq('status', 'voided').order('created_at', { ascending: false }).limit(1);
  return (data && data.length)
    ? `/packets/${data[0].id}`
    : `/packets/new?client=${client.id}&type=private_intake`;
}

// Packet types that belong to each service track. A client who receives
// supplies only has no home-care intake packet of either kind — the GAPP DCH
// forms and the Private Home Care Intake both document care delivered in the
// home. Their paperwork is the CMN and the order, tracked in dme_orders.
export function packetTypesForService(serviceType, serviceLines) {
  if (isDmeOnly({ service_type: serviceType, service_lines: serviceLines })) return [];
  return String(serviceType || '').toLowerCase() === 'gapp'
    ? ['gapp_intake', 'gapp_transfer']
    : ['private_intake'];
}

// When a client's service type changes, void half-built (UNSIGNED) packets of the
// now-wrong track. Anything with a collected signature, or already completed, is
// preserved as a historical record. Works both directions. Returns count voided.
export async function voidStaleDraftPackets(clientId, newServiceType, newServiceLines) {
  const allowed = packetTypesForService(newServiceType, newServiceLines);
  const { data: packets } = await supabase
    .from('document_packets').select('id, packet_type')
    .eq('client_id', clientId)
    .not('status', 'in', '("completed","voided")');
  const wrong = (packets || []).filter((p) => !allowed.includes(p.packet_type));
  let voided = 0;
  for (const p of wrong) {
    const { data: signed } = await supabase.from('signature_slots')
      .select('id').eq('packet_id', p.id).not('signed_at', 'is', null).limit(1);
    if (!signed || signed.length === 0) {
      await supabase.from('document_packets').update({ status: 'voided' }).eq('id', p.id);
      voided++;
    }
  }
  return voided;
}

// Load a packet plus its forms and slots.
export async function getPacketBundle(packetId) {
  const [{ data: packet }, { data: forms }, { data: slots }, { data: tokens }] = await Promise.all([
    supabase.from('document_packets').select('*').eq('id', packetId).single(),
    supabase.from('packet_forms').select('*').eq('packet_id', packetId).order('sort_order'),
    supabase.from('signature_slots').select('*').eq('packet_id', packetId).order('sort_order'),
    // Signing links sent for this packet — sent / viewed facts for the certificate page.
    supabase.from('parent_portal_tokens').select('*').eq('packet_id', packetId).order('created_at'),
  ]);
  const attachments = packet ? await listAttachments(packetId) : [];
  return { packet, forms: forms || [], slots: slots || [], tokens: tokenFacts(tokens), attachments };
}

// Only the audit facts leave the data layer — never the token string itself.
export function tokenFacts(rows) {
  return (rows || []).map((t) => ({
    party_role: t.party_role, sent_to_email: t.sent_to_email || null,
    created_at: t.created_at, accessed_at: t.accessed_at || null, access_count: t.access_count || 0,
  }));
}

export async function listPacketsForClient(clientId) {
  const { data } = await supabase
    .from('document_packets').select('*')
    .eq('client_id', clientId).order('created_at', { ascending: false });
  return data || [];
}

// Packets for a client, each annotated with signature progress (_signed/_total
// over required slots) so the UI can show "3 / 5 signed" at a glance. Excludes voided.
export async function listPacketsWithProgress(clientId) {
  const packets = (await listPacketsForClient(clientId)).filter((p) => p.status !== 'voided');
  if (packets.length === 0) return [];
  const { data: slots } = await supabase
    .from('signature_slots').select('packet_id, required, signed_at')
    .in('packet_id', packets.map((p) => p.id));
  const by = {};
  (slots || []).forEach((s) => {
    if (!s.required) return;
    const b = by[s.packet_id] || { total: 0, signed: 0 };
    b.total += 1; if (s.signed_at) b.signed += 1;
    by[s.packet_id] = b;
  });
  return packets.map((p) => ({ ...p, _signed: by[p.id]?.signed || 0, _total: by[p.id]?.total || 0 }));
}

// Every request agency-wide, newest first, with client and progress — the
// e-Signatures page. status: 'waiting' | 'mine' | 'completed' | 'draft'.
export async function listAllRequests() {
  const { data: packets } = await supabase
    .from('document_packets')
    .select('*, clients(id, client_id, first_name, last_name), pa_cycles(label, drive_folder_id, drive_folder_name)')
    .neq('status', 'voided').order('created_at', { ascending: false }).limit(300);
  if (!packets?.length) return [];
  const { data: slots } = await supabase.from('signature_slots').select('packet_id, party_role, required, signed_at').in('packet_id', packets.map((p) => p.id));
  const by = {};
  (slots || []).forEach((s) => {
    const b = by[s.packet_id] || { total: 0, signed: 0, staffOpen: 0, externalOpen: 0 };
    if (s.required) { b.total += 1; if (s.signed_at) b.signed += 1; else if ([PARTY.CLIENT, PARTY.PHYSICIAN, PARTY.EXTERNAL].includes(s.party_role)) b.externalOpen += 1; else b.staffOpen += 1; }
    by[s.packet_id] = b;
  });
  return packets.map((p) => ({ ...p, _progress: by[p.id] || { total: 0, signed: 0, staffOpen: 0, externalOpen: 0 } }));
}

// Switch a packet between English and Spanish. Only the language of the
// documents changes — the forms, fields and signatures already collected stay
// exactly as they are.
export async function setPacketLanguage(packetId, language) {
  const lang = normalizeLang(language);
  const { error } = await supabase.from('document_packets').update({ language: lang }).eq('id', packetId);
  if (error) throw error;
  return lang;
}

// Save a single form's field_data (merge).
export async function saveFormData(formId, fieldData) {
  const { error } = await supabase
    .from('packet_forms')
    .update({ field_data: fieldData, status: 'complete' })
    .eq('id', formId);
  if (error) throw error;
}

// Record a signature on a slot — with the signer's answers where the document
// asks one (the LON's agree / disagree), as someone signing in person gives.
export async function signSlot(slotId, signedName, { byUser = null, choices = null } = {}) {
  const { error } = await supabase
    .from('signature_slots')
    .update({ signed_name: signedName, signed_at: new Date().toISOString(), signed_by: byUser, ...(choices ? { choices } : {}) })
    .eq('id', slotId);
  if (error) throw error;
}

// Are all required slots signed?
export function isPacketComplete(slots) {
  return (slots || []).filter((s) => s.required).every((s) => !!s.signed_at);
}

// Mark a packet completed. For a per-document GAPP request the link stored
// here is the signature certificate (the documents carry their own links on
// packet_forms); for a private packet it is the single PDF.
export async function finalizePacket(packetId, { fileId, webViewLink }) {
  const { error } = await supabase
    .from('document_packets')
    .update({
      status: 'completed',
      completed_at: new Date().toISOString(),
      final_pdf_id: fileId,
      final_pdf_url: webViewLink,
    })
    .eq('id', packetId);
  if (error) throw error;
}
